import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { getGoals, createGoal, updateGoal, deleteGoal, type Goal } from '../api/goals'

export default function GoalsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showCompleted, setShowCompleted] = useState(false)
  const [isAddingGoal, setIsAddingGoal] = useState(false)
  const [newGoalText, setNewGoalText] = useState('')

  // Fetch goals
  const { data, isLoading } = useQuery({
    queryKey: ['goals', showCompleted],
    queryFn: () => getGoals(!showCompleted),
  })

  // Create goal mutation
  const createMutation = useMutation({
    mutationFn: (surface_goal: string) => createGoal({ surface_goal }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] })
      setNewGoalText('')
      setIsAddingGoal(false)
    },
  })

  // Update goal mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { progress?: number; status?: 'active' | 'completed' | 'abandoned' } }) =>
      updateGoal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] })
    },
  })

  // Delete goal mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteGoal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] })
    },
  })

  const handleAddGoal = () => {
    if (newGoalText.trim()) {
      createMutation.mutate(newGoalText.trim())
    }
  }

  const handleToggleComplete = (goal: Goal) => {
    updateMutation.mutate({
      id: goal.id,
      data: { status: goal.status === 'completed' ? 'active' : 'completed' },
    })
  }

  const handleUpdateProgress = (goal: Goal, progress: number) => {
    updateMutation.mutate({
      id: goal.id,
      data: { progress },
    })
  }

  return (
    <div data-testid="goals-page" className="min-h-screen bg-gradient-calm">
      {/* Header */}
      <header className="glass border-b border-neutral-200/50 sticky top-0 z-10 safe-area-pt">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button
              aria-label="Назад"
              onClick={() => navigate('/chat')}
              className="p-2 -ml-2 hover:bg-neutral-100 rounded-full transition-colors"
            >
              <svg className="w-6 h-6 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="ml-2 text-lg font-semibold text-neutral-800">Мои цели</h1>
          </div>

          {/* Toggle completed */}
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            className={clsx(
              'text-sm px-3 py-1.5 rounded-full transition-colors',
              showCompleted
                ? 'bg-primary-100 text-primary-700'
                : 'text-neutral-500 hover:bg-neutral-100'
            )}
          >
            {showCompleted ? 'Скрыть завершённые' : 'Показать все'}
          </button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4 animate-fade-in safe-area-pb">
        {/* Add goal button/form */}
        {isAddingGoal ? (
          <div className="card">
            <textarea
              value={newGoalText}
              onChange={(e) => setNewGoalText(e.target.value)}
              placeholder="Опишите вашу цель..."
              className="w-full input min-h-[100px] resize-none"
              autoFocus
            />
            <div className="mt-3 flex gap-2 justify-end">
              <button
                onClick={() => {
                  setIsAddingGoal(false)
                  setNewGoalText('')
                }}
                className="btn-ghost text-sm"
              >
                Отмена
              </button>
              <button
                onClick={handleAddGoal}
                disabled={!newGoalText.trim() || createMutation.isPending}
                className="btn-primary text-sm"
              >
                {createMutation.isPending ? 'Добавляем...' : 'Добавить'}
              </button>
            </div>
          </div>
        ) : (
          <button
            data-testid="add-goal-btn"
            onClick={() => setIsAddingGoal(true)}
            className="w-full card hover:shadow-soft-lg transition-shadow flex items-center justify-center gap-2 py-6 text-primary-600"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Добавить цель
          </button>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Goals list */}
        {data?.goals.map((goal) => (
          <GoalCard
            key={goal.id}
            goal={goal}
            onToggleComplete={() => handleToggleComplete(goal)}
            onUpdateProgress={(progress) => handleUpdateProgress(goal, progress)}
            onDelete={() => deleteMutation.mutate(goal.id)}
            isUpdating={updateMutation.isPending || deleteMutation.isPending}
          />
        ))}

        {/* Empty state */}
        {!isLoading && data?.goals.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="font-medium text-neutral-800 mb-1">Нет целей</h3>
            <p className="text-sm text-neutral-500">
              {showCompleted
                ? 'У вас пока нет целей. Добавьте первую!'
                : 'Нет активных целей. Добавьте новую или посмотрите завершённые.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// Goal card component
interface GoalCardProps {
  goal: Goal
  onToggleComplete: () => void
  onUpdateProgress: (progress: number) => void
  onDelete: () => void
  isUpdating: boolean
}

function GoalCard({ goal, onToggleComplete, onUpdateProgress, onDelete, isUpdating }: GoalCardProps) {
  const [showActions, setShowActions] = useState(false)
  const isCompleted = goal.status === 'completed'

  const progressPercent = Math.round(goal.progress * 100)

  return (
    <div
      data-testid="goal-card"
      className={clsx(
        'card relative overflow-hidden transition-opacity',
        isCompleted && 'opacity-70',
        isUpdating && 'pointer-events-none opacity-50'
      )}
    >
      {/* Progress bar background */}
      <div
        className="absolute inset-0 bg-success/10 transition-all duration-300"
        style={{ width: `${progressPercent}%` }}
      />

      <div className="relative">
        {/* Header */}
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <button
            data-testid="goal-toggle"
            onClick={onToggleComplete}
            className={clsx(
              'flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all mt-0.5',
              isCompleted
                ? 'border-success bg-success'
                : 'border-neutral-300 hover:border-primary-400'
            )}
          >
            {isCompleted && (
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>

          {/* Goal text */}
          <div className="flex-1 min-w-0">
            <p className={clsx(
              'font-medium text-neutral-800',
              isCompleted && 'line-through text-neutral-500'
            )}>
              {goal.surface_goal}
            </p>
            {goal.underlying_need && (
              <p className="text-sm text-neutral-500 mt-1">
                {goal.underlying_need}
              </p>
            )}
          </div>

          {/* Menu button */}
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-1.5 -mr-1.5 text-neutral-500 hover:text-neutral-700 rounded-full hover:bg-neutral-100"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01" />
            </svg>
          </button>
        </div>

        {/* Progress slider (only for active goals) */}
        {!isCompleted && (
          <div className="mt-4 flex items-center gap-3">
            <input
              data-testid="goal-progress"
              type="range"
              min="0"
              max="100"
              value={progressPercent}
              onChange={(e) => onUpdateProgress(Number(e.target.value) / 100)}
              className="flex-1 h-2 bg-neutral-200 rounded-full appearance-none cursor-pointer accent-primary-500"
            />
            <span className="text-sm font-medium text-neutral-600 w-12 text-right">
              {progressPercent}%
            </span>
          </div>
        )}

        {/* Actions dropdown */}
        {showActions && (
          <div className="mt-3 pt-3 border-t border-neutral-100 flex gap-2">
            <button
              onClick={onDelete}
              className="btn-ghost text-sm text-error-dark"
            >
              Удалить
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
